"use client";

import { DateTime } from "luxon";

import {
  buildUshvMkcOutreachAssignmentNote,
  buildUshvMkcOutreachNoResponseNote,
} from "@/configuration/groups/ushv-mkc/comms-escalation";
import { USHV_MKC_MAPPINGS } from "@/configuration/groups/ushv-mkc/document-mappings";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent, CopyIcon } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/src/components/shadcn/ui/table";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useFaxLocationAssignment } from "@/src/lib/data-fetchers/location-assignment-fetcher";
import { usePatientAppointments } from "@/src/lib/data-fetchers/scheduling-fetchers";
import { npiRegistryContactPhoneAndFax } from "@/src/lib/practitioner-registry-data";
import { renderTemplate } from "@/src/lib/templating";
import { formatDateTimeWithTimezoneForDisplay } from "@/src/lib/utils/date-and-phone-utils";
import { Classification } from "@/src/models/constants/classifications";
import { Referral } from "@/src/models/documents/referral";

const MKC_DISPLAY_TIMEZONE = "America/Chicago";

function MKCReferralInstructionsContent({
  referral,
  taskId,
  assignee,
}: {
  referral: Referral;
  taskId: string;
  assignee: string;
}) {
  const { referralLocation: faxReferralLocation, authLocation: faxAuthLocation } = useFaxLocationAssignment(taskId);
  const { appointments, isPending: isAppointmentsPending } = usePatientAppointments(referral.patient?.id);

  const startOfDay = DateTime.now().setZone(MKC_DISPLAY_TIMEZONE).startOf("day");
  const upcomingAppointments = appointments?.filter((appointment) => {
    const appointmentDate = DateTime.fromISO(appointment.appointmentDatetime || "").setZone(MKC_DISPLAY_TIMEZONE);

    return appointmentDate.isValid && appointmentDate >= startOfDay;
  });

  const referralMapping = USHV_MKC_MAPPINGS[Classification.REFERRAL];
  const documentLabel = referralMapping
    ? renderTemplate(referralMapping.documentLabel, (referral as unknown as Record<string, unknown>) || "", {
        subdomain: ConfigSubdomain.USHV_MKC,
      })
    : "";

  const referringProviderContact = npiRegistryContactPhoneAndFax(referral.referringProvider?.npiRegistryEntry);

  const telEncounterTemplate = `Hello, could you please add the following referring provider to eCW for this patient's referral?

Provider First Name: ${referral.referringProvider?.givenName || "N/A"}
Provider Last Name: ${referral.referringProvider?.familyName || "N/A"}
Phone number: ${referral.referringLocation?.phoneNumber || referringProviderContact.phone || "N/A"}
Fax number: ${referral.referringLocation?.faxPhoneNumber || referringProviderContact.fax || "N/A"}
NPI number: ${referral.referringProvider?.canonicalNpiNumber || "N/A"}

Please let me know if you need anything else from me, thank you so much!

Best, [ your name ]`;

  const outreachAssignmentNote = buildUshvMkcOutreachAssignmentNote(
    referral.referringProvider,
    referral.receivingProvider
  );
  const outreachNoResponseNote = buildUshvMkcOutreachNoResponseNote(
    referral.referringProvider,
    referral.receivingProvider
  );

  return (
    <div className="flex flex-col gap-4">
      <span className="font-medium text-center">{"USHV MKC"} Referral Instructions:</span>
      {documentLabel && (
        <div>
          <span className="font-semibold text-foreground">Document Label:</span>{" "}
          <CopyableContent text={documentLabel} />
        </div>
      )}
      <div className="text-muted-foreground space-y-4">
        {/* Referral Workflow Steps */}
        <div>
          <span className="font-semibold">Referral Workflow:</span>
          <ol className="mt-2 ml-4 list-decimal list-inside space-y-1">
            <li>Create or update the patient in eCW</li>
            <li>
              Upload file to the <span className="font-medium">Referral Document</span> folder
            </li>
            <li>
              Assign document to <CopyableContent text={assignee} />
            </li>
          </ol>
        </div>

        {/* Outreach Assignment */}
        <div>
          <span className="font-semibold">Assign to Referral Coordinator:</span>
          <div className="mt-2 ml-4 flex flex-col gap-2">
            <span>Assign document to a referral coordinator with the following note:</span>
            <div className="p-3 border rounded-md bg-muted/50 text-sm whitespace-pre-wrap relative group">
              {outreachAssignmentNote}
              <CopyIcon text={outreachAssignmentNote} />
            </div>
          </div>
        </div>

        {/* No Response Escalation */}
        <div>
          <span className="font-semibold">If Patient Doesn&apos;t Respond:</span>
          <div className="mt-2 ml-4 flex flex-col gap-2">
            <span>Reassign/escalate the document back to referral coordinators with the following note:</span>
            <div className="p-3 border rounded-md bg-muted/50 text-sm whitespace-pre-wrap relative group">
              {outreachNoResponseNote}
              <CopyIcon text={outreachNoResponseNote} />
            </div>
          </div>
        </div>

        {/* Add Referring Provider */}
        <div>
          <span className="font-semibold">If Referring Provider is Missing:</span>
          <div className="mt-2 ml-4 flex flex-col gap-2">
            <span>
              Send the following tel encounter to <CopyableContent text={assignee} />:
            </span>
            <div className="p-3 border rounded-md bg-muted/50 text-sm whitespace-pre-wrap relative group">
              {telEncounterTemplate}
              <CopyIcon text={telEncounterTemplate} />
            </div>
          </div>
        </div>

        {/* Add Insurance - only show if coverage is present on referral */}
        {(referral.primaryCoverage || referral.secondaryCoverage) && (
          <div>
            <span className="font-semibold">Add insurance:</span>
            <div className="ml-4 mt-1 text-muted-foreground">
              <span>Insurance in referral has not been added to patient chart. Please add insurance.</span>
            </div>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <div>
          <span className="font-semibold text-foreground">Referral Location:</span>{" "}
          <CopyableContent text={faxReferralLocation || "N/A"} />
        </div>
        <div>
          <span className="font-semibold text-foreground">Auth Location:</span>{" "}
          <CopyableContent text={faxAuthLocation || "N/A"} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-foreground">Scheduling recommendation:</span>
          <ReferralSchedulingRecommendation referral={referral} title="" />
        </div>
      </div>

      {/* Future Encounters */}
      <div>
        <span className="font-semibold text-foreground">Future Encounters:</span>
        {isAppointmentsPending ? (
          <span className="ml-2 text-xs text-muted-foreground">Checking for upcoming encounters...</span>
        ) : upcomingAppointments?.length ? (
          <Table className="mt-2 text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingAppointments.map((appointment) => (
                <TableRow key={appointment.id}>
                  <TableCell>
                    {formatDateTimeWithTimezoneForDisplay(appointment.appointmentDatetime, MKC_DISPLAY_TIMEZONE, false)}
                  </TableCell>
                  <TableCell>{appointment.ehrType || "N/A"}</TableCell>
                  <TableCell>{appointment.ehrReason || "N/A"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <span className="ml-2 text-xs text-muted-foreground">No upcoming encounters found</span>
        )}
      </div>
    </div>
  );
}

interface MKCReferralInstructionsProps {
  referral: Referral;
  taskId: string;
  assignee: string;
}

export function MKCReferralInstructions({ referral, taskId, assignee }: MKCReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <MKCReferralInstructionsContent referral={referral} taskId={taskId} assignee={assignee} />
        </div>
      </EhrInstructionsCard>

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage!} type="Primary" isEditable />}
      {referral.secondaryCoverage && (
        <CoverageCard coverage={referral.secondaryCoverage!} type="Secondary" isEditable />
      )}
    </div>
  );
}
